import { Routes } from '@angular/router';
import { Role } from './model/enum/role';

import { authGuard } from './core/guards/auth.guard';
import { onboardingGuard } from './core/guards/onboarding.guard';
import { roleGuard } from './core/guards/role.guard';
import { guestGuard } from './core/guards/guest.guard';
import { landingPageGuard } from './core/guards/landing-page.guard';
import { offlineGuard } from './core/guards/offline.guard';
import { fnbStoreGuard } from './core/guards/fnb-store.guard';
import { retailStoreGuard } from './core/guards/retail-store.guard';
import { NotFoundComponent } from './features/error/not-found/not-found.component';
import { UnauthorizedComponent } from './features/error/unauthorized/unauthorized.component';
import { DefaultLayout } from './layouts/default-layout/default-layout.component';

export const routes: Routes = [
  {
    path: '',
    component: DefaultLayout,
    canActivate: [authGuard, roleGuard, offlineGuard],
    canActivateChild: [authGuard, roleGuard, offlineGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [landingPageGuard],
        children: []
      },
      {
        path: 'profile',
        title: 'Profile',
        loadChildren: async () =>
          await import('./features/profile/profile.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'notifications',
        title: 'Notifications',
        loadChildren: async () =>
          await import('./features/notifications/notifications.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadChildren: async () =>
          await import('./features/dashboard/dashboard.routes'),
        data: { roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER] },
      },
      {
        path: 'transactions',
        title: 'Transactions',
        loadChildren: async () =>
          await import('./features/transactions/transactions.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'expenses',
        title: 'Expenses',
        loadComponent: () =>
          import('./features/expenses/expenses.component').then(
            (m) => m.ExpensesComponent,
          ),
        data: { roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER] },
      },
      {
        path: 'kitchen',
        title: 'Kitchen',
        loadChildren: async () =>
          await import('./features/kitchen/kitchen.routes'),
        canActivateChild: [fnbStoreGuard],
        data: {
          roles: [
            Role.SUPER_ADMIN,
            Role.ADMIN,
            Role.MANAGER,
            Role.CASHIER,
            Role.KITCHEN,
          ],
        },
      },
      {
        path: 'stores',
        title: 'Stores',
        loadChildren: async () =>
          await import('./features/stores/stores.routes'),
        data: { roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER] },
      },
      {
        path: 'products',
        title: 'Products',
        loadChildren: async () =>
          await import('./features/products/products.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER],
        },
      },
      {
        path: 'barcodes',
        title: 'Barcodes',
        loadChildren: async () =>
          await import('./features/barcodes/barcodes.routes'),
        canActivateChild: [retailStoreGuard],
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'discounts',
        title: 'Discounts',
        loadChildren: async () =>
          await import('./features/discounts/discounts.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'customers',
        title: 'Customers',
        loadChildren: async () =>
          await import('./features/customers/customers.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER],
        },
      },
      {
        path: 'suppliers',
        title: 'Suppliers',
        loadChildren: async () =>
          await import('./features/suppliers/suppliers.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER],
        },
      },
      {
        path: 'feedback',
        title: 'Feedback',
        loadChildren: async () =>
          await import('./features/feedback/feedback.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'analytics',
        title: 'Analytics',
        loadChildren: async () =>
          await import('./features/analytics/analytics.routes'),
        data: { roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER] },
      },
      {
        path: 'registers',
        title: 'Registers',
        loadChildren: async () =>
          await import('./features/registers/registers.routes'),
        canActivate: [authGuard],
      },
      {
        path: 'support',
        title: 'Support',
        loadChildren: async () =>
          await import('./features/support/support.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'users',
        title: 'Users',
        loadChildren: async () => await import('./features/users/users.routes'),
        data: { roles: [Role.SUPER_ADMIN, Role.ADMIN] },
      },
      {
        path: 'inventory',
        title: 'Inventory',
        loadChildren: async () =>
          await import('./features/inventory/inventory.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'attendances',
        title: 'Attendances',
        loadChildren: async () =>
          await import('./features/attendance/attendance.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'feature-request',
        title: 'Feature Request',
        loadChildren: async () =>
          await import('./features/feature-request/feature-request.routes'),
        data: {
          roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER, Role.CASHIER],
        },
      },
      {
        path: 'compliance',
        title: 'Compliance',
        loadChildren: async () =>
          await import('./features/compliance/compliance.routes'),
        data: { roles: [Role.SUPER_ADMIN, Role.ADMIN] },
      },
      {
        path: 'companies',
        title: 'Companies',
        loadChildren: async () =>
          await import('./features/companies/companies.routes'),
        data: {
          roles: [Role.SUPER_ADMIN],
        },
      },
      {
        path: 'plans',
        title: 'Plans',
        loadChildren: async () => await import('./features/plans/plans.routes'),
        data: {
          roles: [Role.SUPER_ADMIN],
        },
      },
      {
        path: 'subscriptions',
        title: 'Subscriptions',
        loadChildren: async () =>
          await import('./features/subscriptions/subscriptions.routes'),
        data: {
          roles: [Role.SUPER_ADMIN],
        },
      },
    ],
  },
  {
    path: 'login',
    title: 'Login',
    loadComponent: async () =>
      (await import('./features/login/login.component')).LoginComponent,
    canActivate: [guestGuard],
    data: { layout: 'auth' },
  },
  {
    path: 'register',
    title: 'Register',
    loadComponent: async () =>
      (await import('./features/register/register.component'))
        .RegisterComponent,
    canActivate: [guestGuard],
    data: { layout: 'auth' },
  },
  {
    path: 'reset',
    loadComponent: async () =>
      (await import('./features/reset-password/reset-password.component'))
        .ResetPasswordComponent,
    canActivate: [guestGuard],
    data: { layout: 'auth' },
  },
  {
    path: 'change-password',
    loadComponent: async () =>
      (await import('./features/change-password/change-password.component'))
        .ChangePasswordComponent,
    canActivate: [guestGuard],
    data: { layout: 'auth' },
  },
  {
    path: 'verify-email',
    title: 'Verify Email',
    loadComponent: async () =>
      (await import('./features/verify-email/verify-email.component'))
        .VerifyEmailComponent,
    data: { layout: 'auth' },
  },
  {
    path: 'onboarding',
    title: 'Onboarding',
    loadChildren: async () =>
      await import('./features/onboarding/onboarding.routes'),
    canActivate: [authGuard, onboardingGuard],
    data: { roles: [Role.ADMIN], layout: 'onboarding' },
  },

  // TODO: Uncomment this when the shop pages are implemented.
  // {
  //   path: 'stores/:storeName/shop',
  //   loadChildren: async () =>
  //     await import('./features/stores/shop/shop.routes'),
  //   canActivate: [authGuard, roleGuard],
  //   data: {
  //     roles: [Role.SUPER_ADMIN, Role.ADMIN, Role.MANAGER],
  //     layout: 'shop',
  //   },
  // },

  {
    path: 'freeze',
    title: 'On Break',
    data: { layout: 'auth' },
    canActivate: [authGuard, roleGuard],
    loadComponent: async () =>
      (
        await import('./features/attendance/break-freeze/break-freeze.component')
      ).BreakFreezeComponent,
  },
  {
    path: '404',
    title: 'Not Found',
    component: NotFoundComponent,
  },
  {
    path: '401',
    title: 'Unauthorized',
    component: UnauthorizedComponent,
  },

  // Catch-all route for unmatched paths
  { path: '**', component: NotFoundComponent },
];
